/** Judgment queue as a list with one definition, sorted by the helm.md
 * priority; the admitting rule shown per item (ticket body, plan §8). An
 * item disappears once its key is in `overview.suppressedKeys` (the
 * server's `isHelmItemSuppressed` verdict) — optimistically on click, and
 * for real once the next run's overlay agrees. */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import type {
  HelmActionInput,
  HelmJudgmentQueue as HelmJudgmentQueueType,
} from '@a7y/platform-health/helm';
import { ItemActionsMenu } from '../ItemActionsMenu';
import { HELM_TABLE_GUTTER, HelmEmpty, HelmSection } from './HelmSection';

export function JudgmentQueue({
  queue,
  suppressedKeys,
  onAction,
}: {
  queue: HelmJudgmentQueueType;
  suppressedKeys: ReadonlySet<string>;
  onAction: (input: HelmActionInput) => void;
}) {
  const visible = queue.items.filter((item) => !suppressedKeys.has(item.key));

  return (
    <HelmSection title="Judgment queue" lineOn="none">
      <div className="-mt-2 mb-4">
        <p className="text-xs/5 text-zinc-500 dark:text-zinc-400">{queue.definition}</p>
        <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
          {queue.count} waiting
          {queue.oldestDays != null ? ` · oldest ${queue.oldestDays}d` : ''} ·{' '}
          {queue.newSinceLastDecisionBlock} new
        </p>
      </div>
      {visible.length === 0 ? (
        <HelmEmpty>none</HelmEmpty>
      ) : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader>Ticket</TableHeader>
              <TableHeader>State · age · rule</TableHeader>
              <TableHeader></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((item) => (
              <TableRow key={item.key} data-testid="helm-jq-item">
                <TableCell className="min-w-0 break-words">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium hover:underline"
                  >
                    {item.identifier}
                  </a>{' '}
                  <span className="text-zinc-700 dark:text-zinc-300">{item.title}</span>
                </TableCell>
                <TableCell className="text-xs text-zinc-500">
                  {item.stateName} · {item.ageDays}d · {item.admittingRule}
                </TableCell>
                <TableCell>
                  <ItemActionsMenu
                    itemKey={item.key}
                    decision={item.decision}
                    onAction={onAction}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}
